import { NgClass } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { PrefixIconComponent } from 'components/layout/prefix-icon/prefix-icon.component';
import { EditQuestionnaireNameComponent } from 'components/questionnaire/edit-questionnaire-name.component';
import { ListQuestionnairesComponent } from 'components/questionnaire/list-questionnaires.component';
import { SortableDirective } from 'directives/sortable.directive';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { QuestionCategoryType } from 'enums/QuestionCategoryType';
import { ProductModel } from 'models/generated/Entities.GormCrm.Product';
import { QuestionModel } from 'models/generated/Entities.GormCrm.Question';
import { QuestionnaireModel } from 'models/generated/Entities.GormCrm.Questionnaire';
import { QuestionnaireQuestionModel } from 'models/generated/Entities.GormCrm.QuestionnaireQuestion';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { CredentialRequired } from 'services/credential.service';
import { DataTracking } from 'services/data-tracking';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { PushService } from 'services/push.service';
import { QuestionnaireService } from 'services/questionnaire.service';
import { EditQuestionnaireQuestionComponent } from './edit-questionnaire-question.component';

@CredentialRequired(CredentialType.QuestionnaireAccessFull)
@Component({
	selector: 'edit-questionnaire',
	templateUrl: './edit-questionnaire.component.html',
	styleUrls: ['./edit-questionnaire.component.scss'],
	providers: [DataTracking],
	imports: [Layout, SubmitButtonComponent, PrefixIconComponent, NgClass, SortableDirective, CustomInputComponent, EditQuestionnaireQuestionComponent, TranslatePipe]
})
@ComponentUrl('edit-questionnaire')
export class EditQuestionnaireComponent implements OnDestroy {

	showExistingQuestionList = false;

	questionCategoryTypeEnum = QuestionCategoryType;
	inputTypeEnum = InputType;
	private _questionnaireId = '';

	unassignedCompanyQuestions: QuestionModel[] = [];
	unassignedOrderQuestions: QuestionModel[] = [];
	unassignedProductQuestions: QuestionModel[] = [];
	
	@UrlParam
	@Input()
	set questionnaireId(id: string) {
		if (id) {
			this._questionnaireId = id;
			this.getQuestionnaire();
		}
	}
	get questionnaireId() {
		return this._questionnaireId;
	}
	selectedQuestionnaireQuestion: QuestionnaireQuestionModel | null = null;

	productsWithQuestions: ProductModel[] = [];
	questionnaire?: QuestionnaireModel;
	companyQuestionnaireQuestions: QuestionnaireQuestionModel[] = [];
	orderQuestionnaireQuestions: QuestionnaireQuestionModel[] = [];
	productQuestionnaireQuestions: QuestionnaireQuestionModel[] = [];

	private updateSubscription: Subscription;
	constructor(readonly navigation: NavigationService, readonly questionnaireService: QuestionnaireService, readonly pushService: PushService, private readonly tracker: DataTracking) {

		this.updateSubscription = this.pushService.onQuestionnaireQuestionChange.subscribe(event => {
			if (this.questionnaireId === event.data.questionnaireId) {
				this.getQuestionnaire();
			};
		});
	}

	changeQuestionnaireName() {
		this.navigation.openPopup(EditQuestionnaireNameComponent, 'questionnaire.change-name.pop-up-title').then(view => view.questionnaire = this.questionnaire!);
	}

	getQuestionnaire() {
		this.questionnaireService.getQuestionnaireById(this.questionnaireId, this.tracker).then(questionnaire => {

			this.questionnaire = questionnaire;

			this.refreshQuestionnaire();

			this.navigation.openParentView(this, 'edit-questionnaire', ListQuestionnairesComponent)
				.then(listQuestionnaires => {
					listQuestionnaires.selectedQuestionnaire = this.questionnaire;
				});
		});
	}
	
	refreshQuestionnaire() {
		this.selectedQuestionnaireQuestion = null;

		this.productQuestionnaireQuestions = this.questionnaire!.questionnaireQuestions
			.filter(questionnaireQuestion => questionnaireQuestion.question!.category === QuestionCategoryType.Product)
			.sort(this.sortBySortOrder);
		this.companyQuestionnaireQuestions = this.questionnaire!.questionnaireQuestions
			.filter(questionnaireQuestion => questionnaireQuestion.question!.category === QuestionCategoryType.Company)
			.sort(this.sortBySortOrder);
		this.orderQuestionnaireQuestions = this.questionnaire!.questionnaireQuestions
			.filter(questionnaireQuestion => questionnaireQuestion.question!.category === QuestionCategoryType.Order)
			.sort(this.sortBySortOrder);

		this.productsWithQuestions = [];
		this.productQuestionnaireQuestions.forEach(questionnaireQuestion => {
			questionnaireQuestion.question!.questionnaireQuestionProducts.forEach(questionnaireQuestionProduct => {
				let isInList = this.productsWithQuestions.some(product => { return product.id === questionnaireQuestionProduct.product!.id; });
				if (!isInList) this.productsWithQuestions.push(questionnaireQuestionProduct.product!);
			});
		});
			
	}

	sortBySortOrder(a: QuestionnaireQuestionModel, b: QuestionnaireQuestionModel) {
		return (a.sortOrder == undefined ? 0 : a.sortOrder) - (b.sortOrder == undefined ? 0 : b.sortOrder);
	}

	addNewQuestion() {
		this.selectedQuestionnaireQuestion = new QuestionnaireQuestionModel();
		this.selectedQuestionnaireQuestion!.questionnaireId = this.questionnaireId;
		this.showExistingQuestionList = false;
	}

	openExistingQuestions() {
		this.selectedQuestionnaireQuestion = null;
		this.showExistingQuestionList = true;

		this.questionnaireService.getQuestions().then(questions => {
			this.unassignedProductQuestions = questions.filter(question => question.category === QuestionCategoryType.Product &&
				!this.productQuestionnaireQuestions.some(questionnaireQuestion => questionnaireQuestion.questionId === question.id));
			this.unassignedCompanyQuestions = questions.filter(question => question.category === QuestionCategoryType.Company &&
				!this.companyQuestionnaireQuestions.some(questionnaireQuestion => questionnaireQuestion.questionId === question.id));
			this.unassignedOrderQuestions = questions.filter(question => question.category === QuestionCategoryType.Order &&
				!this.orderQuestionnaireQuestions.some(questionnaireQuestion => questionnaireQuestion.questionId === question.id));
		});
	}

	removeQuestionFromQuestionnaire(questionnaireQuestion: QuestionnaireQuestionModel) {
		this.questionnaireService.removeQuestionFromQuestionnaire(questionnaireQuestion);
	}
	
	showQuestionsForProduct(question: QuestionModel, product: ProductModel) {
		return question.questionnaireQuestionProducts.some(qqp => qqp.productId === product.id);
	}

	selectQuestion(questionnaireQuestion: QuestionnaireQuestionModel) {
		this.selectedQuestionnaireQuestion = questionnaireQuestion;
		this.showExistingQuestionList = false;
	}

	requiredStatusChanged(questionnaireQuestion: QuestionnaireQuestionModel) {
		this.questionnaireService.updateQuestionnaireQuestionRequired(
			questionnaireQuestion.questionnaireId!,
			questionnaireQuestion.questionId!,
			questionnaireQuestion.required);
	}

	addQuestionToQuestionnaire(questionId: string) {
		this.questionnaireService.addQuestionToQuestionnaire(questionId, this.questionnaireId);
		this.showExistingQuestionList = false;
	}

	updateQuestionnaireName(name: string) {
		this.questionnaireService.updateQuestionnaireName(this.questionnaireId, name);
	}

	saveQuestionnaireQuestionSorting(questionnaireQuestions: QuestionnaireQuestionModel[]) {
		const questions = questionnaireQuestions.map((question, index) => {
			question.sortOrder = index;
			return Object.assign(new QuestionnaireQuestionModel(), question);
		});
		this.questionnaireService.updateQuestionnaireSortOrder(questions);
	}

	ngOnDestroy() {
		this.updateSubscription.unsubscribe();
	}
}
